import { parse } from '@de/utils/interpolation';
import type { Expression } from '@de/utils/interpolation/types';
import { forEachPlainPropTemplateString } from '@de/utils/binding-resolver/plainPropTreeWalk';
import { warnOnce } from '@de/utils/warnOnce';

const WARN_ONCE_NAMESPACE = 'host-slot-input-privacy';

/**
 * The only `{{context.*}}` keys a host slot input may bind: platform keys that
 * identify no visitor. Mirrors `HOST_SLOT_CONTEXT_ALLOWED_KEYS` in Dynamic
 * Flows `DslLayerOneValidator.ts`, which refuses everything else at publish
 * time; keep the two in lockstep.
 */
export const HOST_SLOT_CONTEXT_ALLOWED_KEYS: ReadonlySet<string> = new Set([
  'tenant',
  'tenantId',
  'flowType',
  'trafficSource',
  'params',
  'headline',
  'subject_label_with_fallback',
]);

const CONTEXT_ROOT = 'context';
const CONTEXT_PARAMS_KEY = 'params';

function readsRestrictedContext(expression: Expression): boolean {
  if (expression.kind === 'functionCall') {
    return (
      expression.arg !== undefined && readsRestrictedContext(expression.arg)
    );
  }
  const [root, first, second] = expression.segments;
  if (root?.type !== 'key' || root.name !== CONTEXT_ROOT) return false;
  if (
    first?.type !== 'key' ||
    !HOST_SLOT_CONTEXT_ALLOWED_KEYS.has(first.name)
  ) {
    return true;
  }
  return first.name === CONTEXT_PARAMS_KEY && second?.type !== 'key';
}

/** A template that cannot be parsed counts as restricted. */
function bindsRestrictedContext(value: unknown): boolean {
  let restricted = false;
  forEachPlainPropTemplateString(value, new WeakSet(), (template) => {
    if (restricted || !template.includes('{{')) return;
    try {
      restricted = parse(template).segments.some(
        (segment) =>
          segment.type === 'expression' && readsRestrictedContext(segment),
      );
    } catch {
      restricted = true;
    }
  });
  return restricted;
}

/**
 * Drops each authored `inputs` entry that binds flow context outside
 * {@link HOST_SLOT_CONTEXT_ALLOWED_KEYS}, the whole `context`, or the whole
 * `context.params`, before any template resolves. Publish-time validation is
 * the primary control; this keeps a config that bypassed it from handing a
 * partner the lead, an identity token or tracking ids.
 */
export function dropRestrictedContextInputs(
  props: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  const inputs = props.inputs;
  if (inputs === null || typeof inputs !== 'object' || Array.isArray(inputs)) {
    return props;
  }
  let dropped = false;
  const kept: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(inputs)) {
    if (bindsRestrictedContext(value)) {
      dropped = true;
      warnOnce(
        WARN_ONCE_NAMESPACE,
        `[host-slot] Dropping input "${name}": it binds flow context a host slot cannot receive`,
      );
      continue;
    }
    kept[name] = value;
  }
  return dropped ? { ...props, inputs: kept } : props;
}
